import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta, { getArticleSchema, getBreadcrumbSchema } from '@/components/feature/SEOMeta';
import { articleData, hotVideos } from '@/mocks/data';
import { useState } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleData[id as keyof typeof articleData] : null;
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-16 text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </span>
            <h1 className="font-heading text-2xl text-foreground-950 mb-3">内容未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的内容不存在或已被移除。</p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedVideos = hotVideos.filter(v => v.id.toString() !== id).slice(0, 4);
  const articleSchema = getArticleSchema({
    title: article.title,
    description: article.description,
    image: article.image,
    url: `/article/${id}`,
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: '/' },
    { name: article.category, url: `/category/${getCategorySlug(article.category)}` },
    { name: article.title, url: `/article/${id}` },
  ]);

  return (
    <>
      <SEOMeta
        title={`${article.title} - 芒果视频免费在线观看 | 芒果视频`}
        description={article.description}
        keywords={`芒果视频,${article.title},${article.title}免费在线观看,${article.title}免费播放,芒果视频免费`}
        canonicalUrl={`/article/${id}`}
        ogType="article"
        ogImage={article.image}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${getCategorySlug(article.category)}` },
                { label: article.title },
              ]}
            />

            <article className="mt-6">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title} - 芒果视频免费在线观看`}
                      title={`${article.title} - 芒果视频免费在线观看`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary-500 text-white text-sm px-3 py-1 rounded-full whitespace-nowrap">
                        {article.rating}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-xl md:text-3xl text-foreground-950 mb-2">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="bg-accent-100 text-accent-700 text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="text-sm text-foreground-500">{article.year}</span>
                    <span className="text-sm text-foreground-500">{article.region}</span>
                    <span className="text-sm text-foreground-500">
                      <span className="w-4 h-4 inline-flex items-center justify-center">
                        <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      </span>
                      {article.rating}
                    </span>
                  </div>

                  <div className="space-y-2 mb-5 text-sm text-foreground-600">
                    <p><strong className="text-foreground-900">导演：</strong>{article.director}</p>
                    <p><strong className="text-foreground-900">主演：</strong>{article.cast}</p>
                    <p><strong className="text-foreground-900">状态：</strong><span className="text-primary-600 font-medium">{article.updateTo}</span></p>
                  </div>

                  <div>
                    <p className={`text-sm text-foreground-600 leading-relaxed ${showFullDescription ? '' : 'line-clamp-3'}`}>
                      {article.description}
                    </p>
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-primary-500 text-sm mt-1 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      {showFullDescription ? '收起' : '展开更多'}
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mt-5 flex-wrap">
                    <button className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-fill text-sm"></i>
                      </span>
                      立即播放
                    </button>
                    <button className="bg-background-100 hover:bg-background-200 text-foreground-700 px-4 py-2.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line text-sm"></i>
                      </span>
                      收藏
                    </button>
                    <button className="bg-background-100 hover:bg-background-200 text-foreground-700 px-4 py-2.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-share-line text-sm"></i>
                      </span>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8 md:mt-10">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4">内容详情</h2>
                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4 bg-background-100 rounded-xl p-5 md:p-6"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>

              {relatedVideos.length > 0 && (
                <div className="mt-10 md:mt-14">
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5" data-product-shop="">
                    {relatedVideos.map((video) => (
                      <Link
                        key={video.id}
                        to={video.link}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5">
                          <img
                            src={video.image}
                            alt={`${video.title} - 芒果视频免费在线观看`}
                            title={`${video.title} - 芒果视频免费在线观看`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute top-2 left-2">
                            <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                              {video.badge}
                            </span>
                          </div>
                          <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-yellow-400 text-xs px-1.5 py-0.5 rounded">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-[10px]"></i>
                            </span>
                            {video.rating}
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                          {video.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{video.category} · {video.updateTo}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function getCategorySlug(categoryName: string): string {
  const map: Record<string, string> = {
    '电影': 'movie',
    '电视剧': 'tv',
    '综艺': 'variety',
    '动漫': 'anime',
    '纪录片': 'documentary',
    '少儿': 'kids',
  };
  return map[categoryName] || 'all';
}